import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';

export const supabaseConfigured = !!(supabaseUrl && supabaseAnonKey);

export const supabase: SupabaseClient = supabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    })
  : createClient('https://placeholder.supabase.co', 'placeholder-key', {
      auth: { autoRefreshToken: false, persistSession: false },
    });

/**
 * Tab-resume guard.
 *
 * Supabase's internal auto-refresh timer doesn't fire reliably while
 * the tab is in background. After ~30+ minutes idle the user comes
 * back, types a query, and `getSession()` stalls for 10s+ before our
 * api.ts withTimeout fires — which manifests as "the chat hangs until
 * I refresh." Eagerly kicking refreshSession() on visibility change
 * recovers the token in the background before the user even types.
 *
 * No-op when Supabase isn't configured (storybook / local dev).
 */
if (typeof document !== 'undefined' && supabaseConfigured) {
  let lastRefreshAt = 0;
  let lastHiddenAt = 0;
  // After this much hidden time, any in-flight SSE stream is almost
  // certainly hung on a dead socket — Chrome/Safari aggressively
  // suspend background tab network IO. Dispatching the
  // `altai:tab-resumed-after-idle` event lets useSSE abort the stream
  // so the user gets a clean error instead of a stuck "thinking…"
  // spinner. 5 min is the safe lower bound: shorter and we'd kill
  // streams that are slow but alive.
  const RESUME_AFTER_IDLE_MS = 5 * 60_000;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      lastHiddenAt = Date.now();
      return;
    }
    if (document.visibilityState !== 'visible') return;

    const now = Date.now();
    const hiddenFor = lastHiddenAt > 0 ? now - lastHiddenAt : 0;
    lastHiddenAt = 0;

    // Throttle — don't hammer Supabase if the user flips tabs rapidly.
    if (now - lastRefreshAt >= 60_000) {
      lastRefreshAt = now;
      // Fire-and-forget. Errors are swallowed: we'd rather hand the
      // user a stale token than block the visible page on a refresh
      // race. The next apiFetch will re-resolve via getSession anyway.
      void supabase.auth.refreshSession().catch(() => {});
    }

    // If we were hidden long enough that any open stream is suspect,
    // signal listeners (useSSE) to abort. Fire after the refresh kick
    // so the user's retry has a fresh token to work with.
    if (hiddenFor > RESUME_AFTER_IDLE_MS) {
      window.dispatchEvent(new CustomEvent('altai:tab-resumed-after-idle'));
    }
  });
}
